import RoutePageTitle from "components/RoutePageTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import * as XLSX from "xlsx";

const API_KEY = "57b2a9ba-4428-48e4-b137-f8337b68595e";
const CRM_API_BASE = `${(
  process.env.REACT_APP_CRM_API_BASE || "https://crm-mqtt-718408119422.asia-east1.run.app"
).replace(/\/+$/, "")}/api/v1`;
const ONLINE_WINDOW_MS = 15 * 60 * 1000;
// Browser uploads go directly to GCS, so they can use the backend 200 MiB limit.
const MAX_AD_VIDEO_SIZE_BYTES = 200 * 1024 * 1024;
const DEVICE_SELECT_ALL_VALUE = "__all_devices__";
const MDM_INFO_QUERY = `
  query GetMdmInfoListV3($email: String!, $ts: String) {
    GetMdmInfoListV3(email: $email, ts: $ts)
  }
`;

const OnlineStatusCell = ({ value }) => (
  <MDBadge
    variant="gradient"
    color={value ? "success" : "error"}
    size="sm"
    badgeContent={value ? "上線" : "離線"}
    container
  />
);

OnlineStatusCell.propTypes = {
  value: PropTypes.bool,
};

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const parseBoolean = (value) => {
  if (typeof value === "boolean") return value;
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "true" || raw === "1" || raw === "yes" || raw === "y" || raw === "是") return true;
  if (raw === "false" || raw === "0" || raw === "no" || raw === "n" || raw === "否") return false;
  return false;
};

const formatYesNo = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return parseBoolean(value) ? "是" : "否";
};

const formatDateTimeToTaipei = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

const DEVICE_COLUMNS = [
  { Header: "mdm關聯", accessor: "mdm_ref" },
  { Header: "MDM ID", accessor: "mdm_id" },
  {
    Header: "上線狀態",
    accessor: "onlineStatus",
    Cell: OnlineStatusCell,
  },
  { Header: "時間戳", accessor: "timestamp" },
  { Header: "Trace ID", accessor: "trace_id" },
  { Header: "Span ID", accessor: "span_id" },
  { Header: "Android ID", accessor: "android_id" },
  { Header: "裝置型號", accessor: "model" },
  { Header: "公網 IP", accessor: "public_ip" },
  { Header: "Android 版本", accessor: "android_version" },
  { Header: "電池電量", accessor: "battery_level" },
  { Header: "App 版本", accessor: "app_version" },
  { Header: "網速", accessor: "network_speed" },
  { Header: "POS 使用者 Email", accessor: "email" },
  { Header: "授權資訊", accessor: "license" },
  { Header: "裝置語言", accessor: "device_user_language" },
  { Header: "裝置名稱", accessor: "device_user_device_name" },
  {
    Header: "是否可更新",
    accessor: "device_user_update_available",
    Cell: ({ value }) => formatYesNo(value),
  },
  { Header: "員工名稱", accessor: "employee_name" },
  { Header: "員工 Email", accessor: "employee_email" },
  { Header: "員工角色", accessor: "employee_role" },
  { Header: "班別是否開啟", accessor: "shift_open" },
  { Header: "功能 - 員工管理", accessor: "function_employee" },
  { Header: "功能 - 收據", accessor: "function_receipts" },
  { Header: "功能 - 班表", accessor: "function_shift" },
  { Header: "功能 - 報表", accessor: "function_report" },
  { Header: "功能 - 支付", accessor: "function_payout" },
  { Header: "PIN 設定-銷售", accessor: "sales_pin" },
  { Header: "PIN 設定-", accessor: "feature_pin" },
  { Header: "PIN 設定-班表", accessor: "shift_pin" },
  { Header: "PIN 設定-員工", accessor: "employee_pin" },
  { Header: "PIN 設定-更新", accessor: "update_pin" },
  { Header: "PIN 設定-Back App", accessor: "back_app_pin" },
  { Header: "PIN 長度", accessor: "pin_length" },
  { Header: "緯度", accessor: "latitude" },
  { Header: "經度", accessor: "longitude" },
  { Header: "應用密碼", accessor: "app_password" },
  { Header: "系統語言", accessor: "language" },
  { Header: "main_menu_timeout", accessor: "main_menu_timeout" },
  { Header: "payment_timeout", accessor: "payment_timeout" },
  { Header: "popup_timeout", accessor: "popup_timeout" },
  { Header: "video_timeout", accessor: "video_timeout" },
  {
    Header: "廣告播放啟用",
    accessor: "enable_ad_playback",
    Cell: ({ value }) => formatYesNo(value),
  },
  { Header: "目前廣告影片", accessor: "ad_video_file_name" },
  { Header: "廣告影片更新時間", accessor: "ad_video_updated_at" },
  { Header: "顯示樣式", accessor: "style" },
  { Header: "裝置代碼", accessor: "product_id" },
  { Header: "裝置 ID", accessor: "device_id" },
  { Header: "MAC 位址", accessor: "mac" },
  { Header: "Box 軟體版本", accessor: "box_version" },
  { Header: "連線方式", accessor: "connect_method" },
  { Header: "區網 IP", accessor: "ip" },
  { Header: "WiFi SSID", accessor: "ssid" },
  { Header: "上SKU", accessor: "up_sku" },
  { Header: "下 SKU", accessor: "down_sku" },
  { Header: "is_offline_fallback_enabled", accessor: "is_offline_fallback_enabled" },
  { Header: "國家碼", accessor: "country_code" },
  { Header: "change_img_time", accessor: "change_img_time" },
  { Header: "支付方式", accessor: "payment_method" },
  { Header: "機台編號", accessor: "machine_code" },
  { Header: "機台簡稱", accessor: "machine_name" },
  { Header: "機台序號", accessor: "machine_serial" },
  { Header: "製造商", accessor: "manufacturer" },
  { Header: "安裝日期", accessor: "installation_date" },
  { Header: "機台來源", accessor: "machine_source" },
  { Header: "機台類型", accessor: "machine_type" },
  { Header: "機型代號", accessor: "model_code" },
  { Header: "設備電壓", accessor: "voltage" },
  { Header: "電源插座", accessor: "power_outlet" },
  { Header: "機台尺寸 (cm)", accessor: "dimensions" },
  { Header: "機台重量 (kg)", accessor: "weight" },
  { Header: "是否有螢幕", accessor: "has_screen", Cell: ({ value }) => formatYesNo(value) },
  { Header: "網路使用方式", accessor: "network_type" },
  { Header: "螢幕尺寸 (吋)", accessor: "screen_size" },
  { Header: "貨道總數／儲量", accessor: "capacity" },
  { Header: "機台狀態", accessor: "machine_status" },
  { Header: "合作方", accessor: "partner" },
  { Header: "啟用日期", accessor: "activation_date" },
  { Header: "SIM 卡門號", accessor: "sim_card_number" },
  { Header: "倉別", accessor: "depot" },
  { Header: "是否開立發票", accessor: "invoice_enabled", Cell: ({ value }) => formatYesNo(value) },
  { Header: "創建時間", accessor: "created_at" },
  { Header: "更新時間", accessor: "updated_at" },
  { Header: "dolibarr_id", accessor: "dolibarr_id" },
];

const DeviceExtensionActionCell = ({ row, onOpen }) => (
  <Button
    size="small"
    variant="contained"
    onClick={() => onOpen(row.original)}
    sx={{
      backgroundColor: "#5B3FD1",
      color: "#FFFFFF",
      fontWeight: 600,
      borderRadius: "8px",
      boxShadow: "none",
      "&:hover": { backgroundColor: "#4A33B0", boxShadow: "none" },
    }}
  >
    建立附屬資訊
  </Button>
);

DeviceExtensionActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

const DeviceExtensionActionCellRenderer = ({ row }) => {
  const onOpen = row?.original?.onCreateExtension;
  if (!onOpen) return null;
  return <DeviceExtensionActionCell row={row} onOpen={onOpen} />;
};

DeviceExtensionActionCellRenderer.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
};

const EXTENSION_FIELDS = [
  { key: "mdm_ref", label: "mdm關聯", required: true },
  // { key: "mdm_id", label: "機台識別碼(唯一值)", required: false, type: "number" },
  { key: "machine_code", label: "機器編號", required: false },
  { key: "machine_name", label: "機器名稱", required: false },
  { key: "machine_serial", label: "機器序號" },
  { key: "manufacturer", label: "機台製造商" },
  { key: "installation_date", label: "安裝日期", type: "date" },
  { key: "machine_source", label: "機器來源" },
  { key: "machine_type", label: "機器類別" },
  { key: "model_code", label: "製造商型號" },
  { key: "voltage", label: "設備電壓" },
  { key: "power_outlet", label: "電源插座" },
  { key: "dimensions", label: "機台尺寸 (cm)" },
  { key: "weight", label: "機台重量 (kg)" },
  { key: "has_screen", label: "是否有螢幕", type: "boolean" },
  { key: "network_type", label: "網路使用方式" },
  { key: "screen_size", label: "螢幕尺寸 (吋)" },
  { key: "capacity", label: "貨道總數／儲量" },
  { key: "machine_status", label: "機台狀態" },
  { key: "partner", label: "合作方" },
  { key: "activation_date", label: "啟用日期", type: "date" },
  { key: "sim_card_number", label: "SIM 卡門號" },
  { key: "depot", label: "倉別" },
  { key: "invoice_enabled", label: "是否開立發票", type: "boolean" },
];

const CSV_HEADER_MAP = {
  mdm_ref: "mdm_ref",
  MDM_REF: "mdm_ref",
  "mdm ref": "mdm_ref",
  "mdm_ref(必填)": "mdm_ref",
  mdm_id: "mdm_id",
  "機台識別碼(唯一值)": "mdm_id",
  machine_code: "machine_code",
  機器編號: "machine_code",
  machine_name: "machine_name",
  機器名稱: "machine_name",
  machine_serial: "machine_serial",
  機器序號: "machine_serial",
  manufacturer: "manufacturer",
  機台製造商: "manufacturer",
  installation_date: "installation_date",
  安裝日期: "installation_date",
  machine_source: "machine_source",
  機器來源: "machine_source",
  machine_type: "machine_type",
  機器類別: "machine_type",
  model_code: "model_code",
  製造商型號: "model_code",
  voltage: "voltage",
  設備電壓: "voltage",
  power_outlet: "power_outlet",
  電源插座: "power_outlet",
  dimensions: "dimensions",
  "機台尺寸 (cm)": "dimensions",
  weight: "weight",
  "機台重量 (kg)": "weight",
  has_screen: "has_screen",
  是否有螢幕: "has_screen",
  network_type: "network_type",
  網路使用方式: "network_type",
  screen_size: "screen_size",
  "螢幕尺寸 (吋)": "screen_size",
  capacity: "capacity",
  "貨道總數／儲量": "capacity",
  machine_status: "machine_status",
  機台狀態: "machine_status",
  partner: "partner",
  合作方: "partner",
  activation_date: "activation_date",
  啟用日期: "activation_date",
  sim_card_number: "sim_card_number",
  "SIM 卡門號": "sim_card_number",
  depot: "depot",
  倉別: "depot",
  invoice_enabled: "invoice_enabled",
  是否開立發票: "invoice_enabled",
};

const buildPayload = (row) => {
  const payload = {};
  EXTENSION_FIELDS.forEach((field) => {
    const value = row[field.key];
    if (field.type === "boolean") {
      payload[field.key] = parseBoolean(value);
      return;
    }
    if (field.key === "mdm_id") {
      const num = Number(value);
      payload[field.key] = Number.isNaN(num) ? "" : num;
      return;
    }
    if (value === null || value === undefined) {
      payload[field.key] = "";
      return;
    }
    payload[field.key] = String(value);
  });
  return payload;
};

const mapCsvRows = (rows = []) =>
  rows.map((row) => {
    const mapped = {};
    Object.entries(row || {}).forEach(([header, value]) => {
      const key = CSV_HEADER_MAP[header] || CSV_HEADER_MAP[normalizeHeader(header)] || header;
      mapped[key] = value;
    });
    return buildPayload(mapped);
  });

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getLastTwoDigitSortValue = (value) => {
  const match = String(value || "").match(/(\d{2})A$/i);
  return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
};

const sortByLastTwoDigits = (a, b) => {
  const diff = getLastTwoDigitSortValue(a) - getLastTwoDigitSortValue(b);
  if (diff !== 0) return diff;
  return String(a).localeCompare(String(b), "zh-Hant", { numeric: true });
};

const normalizeSelectValues = (value) => (typeof value === "string" ? value.split(",") : value);

function DeviceInfoBK() {
  const { t } = useTranslation("common");
  const { userData, role } = useUserContext() || {};
  const [rows, setRows] = useState([]);
  const [schemaFields, setSchemaFields] = useState([]);
  const [filterRange, setFilterRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({});
  const [createError, setCreateError] = useState("");
  const [createUploading, setCreateUploading] = useState(false);
  const [batchRows, setBatchRows] = useState([]);
  const [batchResult, setBatchResult] = useState(null);
  const [createSuccess, setCreateSuccess] = useState("");
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdSubmitting, setCmdSubmitting] = useState(false);
  const [cmdError, setCmdError] = useState("");
  const [cmdSuccess, setCmdSuccess] = useState("");
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("csv");
  const [cmdForm, setCmdForm] = useState({
    productIds: [],
    cmd: "",
    app_password: "",
    language: "",
    payment_method: [],
    main_menu_timeout: "",
    payment_timeout: "",
    popup_timeout: "",
    ssid: "",
    password: "",
    videoFile: null,
    enable: false,
    hours: "",
    minutes: "",
    weekday: [],
  });
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const isCMRole = String(role || "").toUpperCase() === "CM";
  const isUpdateAppConfig = cmdForm.cmd === "update_app_config";
  const isUpdateVideo = cmdForm.cmd === "update_video";
  const isPaymentMethodConfig = cmdForm.cmd === "payment_method_config";
  const isTimingReboot = cmdForm.cmd === "timing_reboot";
  const isStockReady = cmdForm.cmd === "stock_ready";

  const commandOptions = useMemo(
    () => [
      { name: "野獸國盒子更新", code: "start_ota" },
      { name: "重啟野獸國盒子", code: "restart" },
      { name: "查詢裝置狀態", code: "get_telemetry" },
      { name: "啟動 APP 更新", code: "update_app" },
      { name: "更新廣告影片", code: "update_video" },
      { name: "進入維護模式", code: "maintenance_mode" },
      { name: "結束維護模式", code: "maintenance_stop" },
      { name: "定時重啟", code: "timing_reboot" },
      { name: "Android 裝置重啟", code: "android_reboot" },
      { name: "更新 App 內部設定", code: "update_app_config" },
      { name: "支付方式", code: "payment_method_config" },
      { name: "清除裝置餘額", code: "flush" },
      { name: "更新產品圖", code: "update_sku_img" },
      { name: "上層出蛋", code: "upper_gacha" },
      { name: "下層出蛋", code: "lower_gacha" },
    ],
    []
  );

  const languageOptions = useMemo(
    () => [
      { name: "中文", code: "zh" },
      { name: "英文", code: "en" },
    ],
    []
  );

  const paymentMethodOptions = useMemo(
    () => [
      { name: "現金", code: "cash" },
      { name: "LINE Pay 離線", code: "linepay-offline" },
      { name: "信用卡", code: "credit-card" },
      { name: "Apple Pay", code: "apple-pay" },
      { name: "Google Pay", code: "google-pay" },
      { name: "綠界支付", code: "ecpay" },
    ],
    []
  );

  const weekdayOptions = useMemo(
    () => [
      { label: "周日", value: 1 },
      { label: "周一", value: 2 },
      { label: "周二", value: 4 },
      { label: "周三", value: 8 },
      { label: "周四", value: 16 },
      { label: "周五", value: 32 },
      { label: "周六", value: 64 },
    ],
    []
  );

  const deviceOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    rows.forEach((row) => {
      const value = row?.product_id;
      if (!value || seen.has(value)) return;
      seen.add(value);
      list.push(value);
    });
    return list.sort(sortByLastTwoDigits);
  }, [rows]);
  const isAllDevicesSelected =
    deviceOptions.length > 0 && cmdForm.productIds.length === deviceOptions.length;
  const isSomeDevicesSelected = cmdForm.productIds.length > 0 && !isAllDevicesSelected;

  const fetchDevices = async () => {
    if (!email) {
      setError("無登入信箱，無法載入資料。");
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: MDM_INFO_QUERY,
          variables: { email, ts: Date.now().toString() },
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Request failed (${res.status})`);
      }
      const json = await res.json();
      if (json?.errors?.length) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }
      const payload = json?.data?.GetMdmInfoListV3;
      const list = Array.isArray(payload?.data) ? payload.data : [];
      const schema = Array.isArray(payload?.schema) ? payload.schema : [];
      let assignmentsByProductID = new Map();
      try {
        const assignmentRes = await fetch(`${CRM_API_BASE}/mdm/ad/assignments`, {
          headers: { "Mdm-Auth": API_KEY },
        });
        if (assignmentRes.ok) {
          const assignmentJson = await assignmentRes.json();
          const assignments = Array.isArray(assignmentJson?.data) ? assignmentJson.data : [];
          assignmentsByProductID = new Map(
            assignments.map((assignment) => [assignment.product_id, assignment])
          );
        }
      } catch (_) {
        // Device information remains available if the optional advertising assignment lookup fails.
      }
      setSchemaFields(schema);
      setFilterRange(payload?.filter_range || null);
      const nowMs = Date.now();
      const withStatus = list.map((item) => {
        const tsMs = Date.parse(item?.timestamp || "");
        const isOnline = Number.isFinite(tsMs) && nowMs - tsMs <= ONLINE_WINDOW_MS;
        const assignment = assignmentsByProductID.get(item?.product_id);
        return {
          ...item,
          onlineStatus: isOnline,
          is_online: isOnline,
          ad_video_file_name: assignment?.file_name || "",
          ad_video_updated_at: assignment?.updated_at || "",
        };
      });
      const sorted = withStatus.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRows(sorted);
    } catch (err) {
      setError(err.message || "載入失敗");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const resetCreateState = useCallback(() => {
    setCreateError("");
    setCreateUploading(false);
    setBatchRows([]);
    setBatchResult(null);
  }, []);

  const resetCmdState = () => {
    setCmdError("");
    setCmdSubmitting(false);
    setCmdForm({
      productIds: [],
      cmd: "",
      app_password: "",
      language: "",
      payment_method: [],
      main_menu_timeout: "",
      payment_timeout: "",
      popup_timeout: "",
      ssid: "",
      password: "",
      videoFile: null,
      enable: false,
      hours: "",
      minutes: "",
      weekday: [],
    });
  };

  const handleOpenCreate = useCallback(
    (row) => {
      const initial = {};
      EXTENSION_FIELDS.forEach((field) => {
        const rawValue = row?.[field.key];
        if (field.type === "boolean") {
          if (rawValue === undefined || rawValue === null || rawValue === "") {
            initial[field.key] = false;
          } else {
            initial[field.key] = Boolean(rawValue);
          }
          return;
        }
        if (field.key === "mdm_id") {
          initial[field.key] = rawValue ?? "";
          return;
        }
        initial[field.key] = rawValue ?? "";
      });
      setCreateForm(initial);
      resetCreateState();
      setCreateOpen(true);
    },
    [resetCreateState]
  );

  const schemaColumns = useMemo(() => {
    if (!Array.isArray(schemaFields) || schemaFields.length === 0) {
      return DEVICE_COLUMNS;
    }
    const columns = schemaFields
      .filter((field) => field && field.visible !== false && field.key)
      .map((field) => {
        const accessor = String(field.key);
        const column = {
          Header: field.label || accessor,
          accessor,
        };
        if (accessor === "onlineStatus" || accessor === "is_online") {
          column.Cell = OnlineStatusCell;
        }
        if (accessor === "updated_at" || accessor === "created_at") {
          column.Cell = ({ value }) => formatDateTimeToTaipei(value);
        }
        if (
          accessor === "invoice_enabled" ||
          accessor === "device_user_update_available" ||
          accessor === "enable_ad_playback"
        ) {
          column.Cell = ({ value }) => formatYesNo(value);
        }
        return column;
      });
    const statusIndex = columns.findIndex((col) => col.accessor === "is_online");
    if (statusIndex > 0) {
      const [statusCol] = columns.splice(statusIndex, 1);
      columns.unshift(statusCol);
    }
    if (!columns.some((column) => column.accessor === "ad_video_file_name")) {
      columns.push({ Header: "目前廣告影片", accessor: "ad_video_file_name" });
    }
    if (!columns.some((column) => column.accessor === "ad_video_updated_at")) {
      columns.push({
        Header: "廣告影片更新時間",
        accessor: "ad_video_updated_at",
        Cell: ({ value }) => formatDateTimeToTaipei(value),
      });
    }
    return columns;
  }, [schemaFields]);

  const tableColumns = useMemo(
    () => [
      {
        Header: "操作",
        accessor: "actions",
        Cell: DeviceExtensionActionCellRenderer,
      },
      ...schemaColumns,
    ],
    [schemaColumns]
  );

  const tableData = useMemo(
    () => ({
      columns: tableColumns,
      rows: rows.map((row) => ({ ...row, onCreateExtension: handleOpenCreate })),
    }),
    [rows, tableColumns, handleOpenCreate]
  );

  const exportColumns = useMemo(
    () =>
      DEVICE_COLUMNS.map((column) => ({
        header: column.Header,
        accessor: column.accessor,
      })),
    []
  );

  const handleCreateChange = (key, value) => {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
    if (createError) setCreateError("");
  };

  const handleCmdChange = (key, value) => {
    setCmdForm((prev) => ({ ...prev, [key]: value }));
    if (cmdError) setCmdError("");
  };

  const handleVideoFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".mp4") || !["video/mp4", ""].includes(file.type)) {
      handleCmdChange("videoFile", null);
      setCmdError("只支援 MP4 格式的廣告影片");
      return;
    }
    if (file.size > MAX_AD_VIDEO_SIZE_BYTES) {
      handleCmdChange("videoFile", null);
      setCmdError("廣告影片檔案不得超過 200 MB");
      return;
    }
    handleCmdChange("videoFile", file);
  };

  const handleDeviceIdsChange = (event) => {
    const value = normalizeSelectValues(event.target.value);
    if (value.includes(DEVICE_SELECT_ALL_VALUE)) {
      handleCmdChange("productIds", isAllDevicesSelected ? [] : deviceOptions);
      return;
    }
    handleCmdChange("productIds", value);
  };

  const handleCmdSubmit = async () => {
    if (!cmdForm.productIds.length) {
      setCmdError("請選擇裝置 ID");
      return;
    }
    if (!cmdForm.cmd) {
      setCmdError("請選擇指令");
      return;
    }
    if (isUpdateVideo && !cmdForm.videoFile) {
      setCmdError("請選擇 MP4 影片檔");
      return;
    }
    if (isUpdateVideo) {
      setCmdSubmitting(true);
      try {
        const uploadRequest = await fetch(`${CRM_API_BASE}/mdm/ad/upload-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Mdm-Auth": API_KEY,
          },
          body: JSON.stringify({
            file_name: cmdForm.videoFile.name,
            content_type: "video/mp4",
            file_size: cmdForm.videoFile.size,
          }),
        });
        const uploadPayload = await uploadRequest.json().catch(() => null);
        if (!uploadRequest.ok || uploadPayload?.code !== 200 || !uploadPayload?.data?.upload_url) {
          throw new Error(
            uploadPayload?.message || `無法建立影片上傳連結 (${uploadRequest.status})`
          );
        }

        const storageUpload = await fetch(uploadPayload.data.upload_url, {
          method: "PUT",
          headers: { "Content-Type": "video/mp4" },
          body: cmdForm.videoFile,
        });
        if (!storageUpload.ok) {
          throw new Error(`影片上傳至 Storage 失敗 (${storageUpload.status})`);
        }

        const deployRequest = await fetch(`${CRM_API_BASE}/mdm/ad/deploy-uploaded`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Mdm-Auth": API_KEY,
          },
          body: JSON.stringify({
            object_name: uploadPayload.data.object_name,
            product_id: cmdForm.productIds,
          }),
        });
        const deployPayload = await deployRequest.json().catch(() => null);
        if (!deployRequest.ok || deployPayload?.code !== 200) {
          throw new Error(deployPayload?.message || `影片部署失敗 (${deployRequest.status})`);
        }
        setCmdSuccess("success");
        await fetchDevices();
        setCmdOpen(false);
        resetCmdState();
      } catch (err) {
        setCmdError(err.message || "影片上傳或更新失敗");
      } finally {
        setCmdSubmitting(false);
      }
      return;
    }
    const toNumber = (value) => {
      const num = Number(String(value || "").trim());
      return Number.isNaN(num) ? 0 : num;
    };
    const payload = {
      product_id: cmdForm.productIds,
      cmd: isPaymentMethodConfig ? "update_app_config" : cmdForm.cmd,
      app_password: cmdForm.app_password,
      language: cmdForm.language,
      payment_method: cmdForm.payment_method,
      main_menu_timeout: toNumber(cmdForm.main_menu_timeout),
      payment_timeout: toNumber(cmdForm.payment_timeout),
      popup_timeout: toNumber(cmdForm.popup_timeout),
      ssid: cmdForm.ssid,
      password: cmdForm.password,
      enable: Boolean(cmdForm.enable),
      hours: toNumber(cmdForm.hours),
      minutes: toNumber(cmdForm.minutes),
      weekday: cmdForm.weekday.reduce((sum, v) => sum + Number(v || 0), 0),
    };
    setCmdSubmitting(true);
    try {
      const res = await fetch(`${CRM_API_BASE}/mdm/cmd`, {
        method: "POST",
        headers: {
          "Mdm-Auth": API_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Request failed (${res.status})`);
      }
      const json = await res.json();
      if (json?.code && json.code !== 200) {
        throw new Error(json.message || "Command failed");
      }
      setCmdSuccess("success");
      setCmdOpen(false);
      resetCmdState();
    } catch (err) {
      setCmdError(err.message || "發送失敗");
    } finally {
      setCmdSubmitting(false);
    }
  };

  const validateCreateForm = (payload) => {
    const required = EXTENSION_FIELDS.filter((f) => f.required).map((f) => f.key);
    for (const key of required) {
      const value = payload[key];
      if (value === "" || value === null || value === undefined) {
        return `欄位 ${key} 必填`;
      }
    }
    return "";
  };

  const submitCreate = async (payload) => {
    const res = await fetch(`${CRM_API_BASE}/mdmextension/create`, {
      method: "POST",
      headers: {
        "Mdm-Auth": API_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Request failed (${res.status})`);
    }
    const json = await res.json();
    if (json?.code && json.code !== 200) {
      throw new Error(json.message || "Create failed");
    }
    return json;
  };

  const handleCreateSubmit = async () => {
    const payload = buildPayload(createForm);
    const err = validateCreateForm(payload);
    if (err) {
      setCreateError(err);
      return;
    }
    try {
      setCreateUploading(true);
      await submitCreate(payload);
      setCreateOpen(false);
      resetCreateState();
      setCreateSuccess("success");
      fetchDevices();
    } catch (e) {
      setCreateError(e.message || "建立失敗");
    } finally {
      setCreateUploading(false);
    }
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;
    resetCreateState();
    const text = await file.text();
    const workbook = XLSX.read(text, { type: "string", raw: true });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      setCreateError("CSV 內容為空");
      return;
    }
    const sheet = workbook.Sheets[sheetName];
    const rowsJson = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
    if (!rowsJson.length) {
      setCreateError("CSV 沒有資料");
      return;
    }
    const mapped = mapCsvRows(rowsJson);
    setBatchRows(mapped);
  };

  const handleBatchUpload = async () => {
    if (!batchRows.length) {
      setCreateError("沒有可上傳的資料");
      return;
    }
    const results = { success: 0, failed: 0, errors: [] };
    setCreateUploading(true);
    try {
      for (let i = 0; i < batchRows.length; i += 1) {
        const payload = batchRows[i];
        const err = validateCreateForm(payload);
        if (err) {
          results.failed += 1;
          results.errors.push({ index: i + 1, message: err });
          continue;
        }
        try {
          await submitCreate(payload);
          results.success += 1;
        } catch (e) {
          results.failed += 1;
          results.errors.push({ index: i + 1, message: e.message || "上傳失敗" });
        }
      }
      setBatchResult(results);
      fetchDevices();
      if (results.failed === 0 && results.success > 0) {
        setCreateOpen(false);
        resetCreateState();
        setCreateSuccess("success");
      } else if (results.success > 0) {
        setCreateSuccess("success");
      }
    } finally {
      setCreateUploading(false);
    }
  };

  const escapeCsvValue = (value) => {
    const raw = value === null || value === undefined ? "" : String(value);
    if (/["\n,]/.test(raw)) {
      return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
  };

  const downloadCsvTemplate = () => {
    const headers = EXTENSION_FIELDS.map((field) => field.key);
    const values = headers.map((key) => escapeCsvValue(createForm[key]));
    const csv = `${headers.join(",")}\n${values.join(",")}\n`;
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "mdmextension_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    try {
      const headers = exportColumns.map((column) => column.header);
      const bodyRows = rows.map((row) =>
        exportColumns.map((column) => {
          const value = row?.[column.accessor];
          if (column.accessor === "onlineStatus") return value ? "上線" : "離線";
          if (column.accessor === "has_screen" || column.accessor === "invoice_enabled") {
            return formatYesNo(value);
          }
          if (value === null || value === undefined) return "";
          if (typeof value === "object") return JSON.stringify(value);
          return value;
        })
      );

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...bodyRows]);

      if (downloadFormat === "xlsx") {
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Device Info");
        XLSX.writeFile(workbook, "device-info.xlsx");
      } else {
        const csv = `\uFEFF${XLSX.utils.sheet_to_csv(worksheet)}`;
        downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "device-info.csv");
      }

      setDownloadDialogOpen(false);
    } catch (err) {
      console.error(err);
      setError("下載失敗");
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [email]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  <RoutePageTitle />
                </MDTypography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    id="download"
                    variant="outlined"
                    onClick={() => {
                      setDownloadFormat("csv");
                      setDownloadDialogOpen(true);
                    }}
                    sx={{
                      borderRadius: "12px",
                      borderColor: "#FFFFFF",
                      color: "#FFFFFF",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#FFFFFF",
                        backgroundColor: "rgba(255,255,255,0.15)",
                      },
                    }}
                  >
                    匯出表格
                  </Button>
                  {!isCMRole && (
                    <Button
                      variant="contained"
                      onClick={() => {
                        resetCmdState();
                        setCmdOpen(true);
                      }}
                      sx={{
                        borderRadius: "12px",
                        backgroundColor: "#FFFFFF",
                        color: "#1A73E8",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "#F0F6FF",
                        },
                      }}
                    >
                      發送裝置控制指令
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={fetchDevices}
                    sx={{
                      borderRadius: "12px",
                      borderColor: "#FFFFFF",
                      color: "#FFFFFF",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#FFFFFF",
                        backgroundColor: "rgba(255,255,255,0.15)",
                      },
                    }}
                  >
                    重新整理
                  </Button>
                </Stack>
              </MDBox>
              <MDBox pt={3} px={0} width="100%">
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="300px"
                  >
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <>
                    {error && (
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="80px"
                      >
                        <MDTypography color="error">{error}</MDTypography>
                      </MDBox>
                    )}
                    <DataTable
                      table={tableData}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>匯出表格</DialogTitle>
        <DialogContent dividers>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={downloadFormat}
              onChange={(event) => setDownloadFormat(event.target.value)}
            >
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
              <FormControlLabel value="xlsx" control={<Radio />} label="XLSX" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDownloadDialogOpen(false)}
            sx={{
              borderRadius: "10px",
              borderColor: "#1A73E8",
              color: "#1A73E8",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#1557B0",
                backgroundColor: "rgba(26, 115, 232, 0.08)",
              },
            }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleDownload}
            sx={{
              borderRadius: "10px",
              backgroundColor: "#1A73E8",
              color: "#FFFFFF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#1557B0",
              },
            }}
          >
            確認下載
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          resetCreateState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>建立裝置附屬資訊</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {createError && (
            <MDTypography color="error" sx={{ mb: 1 }}>
              {createError}
            </MDTypography>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {EXTENSION_FIELDS.map((field) => {
              if (field.type === "boolean") {
                return (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Checkbox
                        checked={Boolean(createForm[field.key])}
                        onChange={(event) => handleCreateChange(field.key, event.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                );
              }
              return (
                <TextField
                  key={field.key}
                  label={`${field.label}${field.required ? " *" : ""}`}
                  value={createForm[field.key] ?? ""}
                  onChange={(event) => handleCreateChange(field.key, event.target.value)}
                  disabled={field.key === "mdm_ref"}
                  type={
                    field.type === "number" ? "number" : field.type === "date" ? "date" : "text"
                  }
                  InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                  fullWidth
                />
              );
            })}

            <MDBox mt={1}>
              <MDTypography variant="subtitle2" sx={{ mb: 1 }}>
                批次上傳 CSV
              </MDTypography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={downloadCsvTemplate}
                  sx={{ color: "#1A73E8", borderColor: "#1A73E8" }}
                >
                  下載範本
                </Button>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ color: "#1A73E8", borderColor: "#1A73E8" }}
                >
                  上傳 CSV
                  <input
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={(event) => handleCsvUpload(event.target.files?.[0])}
                  />
                </Button>
                {batchRows.length > 0 && (
                  <MDTypography variant="caption">已載入 {batchRows.length} 筆</MDTypography>
                )}
              </Stack>
              {batchResult && (
                <MDTypography variant="caption" sx={{ mt: 1, display: "block" }}>
                  成功 {batchResult.success} / 失敗 {batchResult.failed}
                </MDTypography>
              )}
            </MDBox>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false);
              resetCreateState();
            }}
          >
            取消
          </Button>
          {batchRows.length > 0 ? (
            <Button
              variant="contained"
              onClick={handleBatchUpload}
              disabled={createUploading}
              sx={{
                backgroundColor: "#5B3FD1",
                color: "#FFFFFF",
                fontWeight: 600,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#4A33B0", boxShadow: "none" },
              }}
            >
              {createUploading ? "上傳中..." : "批次上傳"}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateSubmit}
              disabled={createUploading}
              sx={{
                backgroundColor: "#5B3FD1",
                color: "#FFFFFF",
                fontWeight: 600,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#4A33B0", boxShadow: "none" },
              }}
            >
              {createUploading ? "送出中..." : "送出"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={cmdOpen}
        onClose={() => {
          setCmdOpen(false);
          resetCmdState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>發送裝置指令</DialogTitle>
        <DialogContent>
          {cmdError && (
            <MDTypography color="error" sx={{ mb: 1 }}>
              {cmdError}
            </MDTypography>
          )}
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="device-id-label">裝置 ID</InputLabel>
              <Select
                labelId="device-id-label"
                multiple
                value={cmdForm.productIds}
                label="裝置 ID"
                onChange={handleDeviceIdsChange}
                renderValue={(selected) => selected.join(", ")}
              >
                <MenuItem value={DEVICE_SELECT_ALL_VALUE} divider>
                  <Checkbox
                    checked={isAllDevicesSelected}
                    indeterminate={isSomeDevicesSelected}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  全選
                </MenuItem>
                {deviceOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    <Checkbox checked={cmdForm.productIds.includes(value)} sx={{ p: 0.5, mr: 1 }} />
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="cmd-label">指令</InputLabel>
              <Select
                labelId="cmd-label"
                value={cmdForm.cmd}
                label="指令"
                onChange={(event) => handleCmdChange("cmd", event.target.value)}
              >
                {commandOptions.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isUpdateAppConfig && (
              <>
                <TextField
                  label="App 密碼"
                  value={cmdForm.app_password}
                  onChange={(event) => handleCmdChange("app_password", event.target.value)}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel id="lang-label">語言代碼</InputLabel>
                  <Select
                    labelId="lang-label"
                    value={cmdForm.language}
                    label="語言代碼"
                    onChange={(event) => handleCmdChange("language", event.target.value)}
                  >
                    {languageOptions.map((option) => (
                      <MenuItem key={option.code} value={option.code}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            {isUpdateVideo && (
              <Stack spacing={1}>
                <Button component="label" variant="outlined">
                  選擇 MP4 廣告影片
                  <input
                    type="file"
                    accept="video/mp4,.mp4"
                    hidden
                    onChange={handleVideoFileChange}
                  />
                </Button>
                <MDTypography variant="caption" color="text">
                  {cmdForm.videoFile
                    ? `已選擇：${cmdForm.videoFile.name}`
                    : "只支援 MP4，檔案不得超過 200 MB；上傳成功後才會更新選取的機台。"}
                </MDTypography>
              </Stack>
            )}
            {isPaymentMethodConfig && (
              <FormControl fullWidth>
                <InputLabel id="payment-method-label">支付方式</InputLabel>
                <Select
                  labelId="payment-method-label"
                  multiple
                  value={cmdForm.payment_method}
                  label="支付方式"
                  onChange={(event) =>
                    handleCmdChange("payment_method", normalizeSelectValues(event.target.value))
                  }
                  renderValue={(selected) =>
                    paymentMethodOptions
                      .filter((option) => selected.includes(option.code))
                      .map((option) => option.name)
                      .join(", ")
                  }
                >
                  {paymentMethodOptions.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      <Checkbox
                        checked={cmdForm.payment_method.includes(option.code)}
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {isUpdateAppConfig && (
              <>
                <TextField
                  label="主選單超時時間(秒)"
                  value={cmdForm.main_menu_timeout}
                  onChange={(event) => handleCmdChange("main_menu_timeout", event.target.value)}
                  type="number"
                  fullWidth
                />
                <TextField
                  label="支付超時時間(秒)"
                  value={cmdForm.payment_timeout}
                  onChange={(event) => handleCmdChange("payment_timeout", event.target.value)}
                  type="number"
                  fullWidth
                />
                <TextField
                  label="彈出視窗超時時間(秒)"
                  value={cmdForm.popup_timeout}
                  onChange={(event) => handleCmdChange("popup_timeout", event.target.value)}
                  type="number"
                  fullWidth
                />
              </>
            )}
            {isStockReady && (
              <>
                <TextField
                  label="Wi-Fi SSID"
                  value={cmdForm.ssid}
                  onChange={(event) => handleCmdChange("ssid", event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Wi-Fi 密碼"
                  value={cmdForm.password}
                  onChange={(event) => handleCmdChange("password", event.target.value)}
                  fullWidth
                />
              </>
            )}
            {isTimingReboot && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(cmdForm.enable)}
                      onChange={(event) => handleCmdChange("enable", event.target.checked)}
                    />
                  }
                  label="啟用"
                />
                <TextField
                  label="時數設定"
                  value={cmdForm.hours}
                  onChange={(event) => handleCmdChange("hours", event.target.value)}
                  type="number"
                  fullWidth
                />
                <TextField
                  label="分鐘設定"
                  value={cmdForm.minutes}
                  onChange={(event) => handleCmdChange("minutes", event.target.value)}
                  type="number"
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel id="weekday-label">星期設定</InputLabel>
                  <Select
                    labelId="weekday-label"
                    multiple
                    value={cmdForm.weekday}
                    label="星期設定"
                    onChange={(event) => handleCmdChange("weekday", event.target.value)}
                  >
                    {weekdayOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCmdOpen(false);
              resetCmdState();
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleCmdSubmit}
            disabled={cmdSubmitting}
            sx={{
              backgroundColor: "#5B3FD1",
              color: "#FFFFFF",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#4A33B0", boxShadow: "none" },
            }}
          >
            {cmdSubmitting ? "送出中..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(createSuccess)}
        autoHideDuration={3000}
        onClose={() => setCreateSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCreateSuccess("")}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {createSuccess}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(cmdSuccess)}
        autoHideDuration={3000}
        onClose={() => setCmdSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCmdSuccess("")}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {cmdSuccess}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default DeviceInfoBK;
